import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Copy, Loader2, Mail, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  INVITABLE_ROLES,
  ROLE_DISPLAY_NAMES,
  type InvitableRole,
} from "@/lib/api-types";
import {
  useInviteMember,
  useMembers,
  useSendInviteEmail,
  useUpdateMemberRole,
  type WorkspaceMember,
} from "@/hooks/use-members";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Feedback =
  | null
  | { kind: "invite"; email: string; inviteUrl: string; reused: boolean }
  | { kind: "roleChanged"; name: string; role: InvitableRole }
  | { kind: "emailSent"; email: string }
  | { kind: "error"; message: string };

/**
 * Manage Members dialog (locked 2026-07-12).
 *
 * ONE input field that plays two different games depending on whether the
 * value matches an existing workspace member:
 *
 *   New person → email → the row below the input becomes the invite
 *   controls: Copy link + Send email. Role dropdown at right of the input
 *   picks the invite tier.
 *
 *   Existing member → clicking a suggestion "selects" them and the button
 *   turns into "Change role" that PATCHes /admin/editors/:id/role.
 *
 * Deliberately NOT a member table with remove buttons — user split that off
 * into a separate future page. Removal + audit history live there, not here.
 *
 * Only Workspace ADMIN reaches this UI (visibility gated by canInviteUsers
 * at the projects page), so no per-action role check inside — the backend
 * enforces admin scope on every endpoint anyway.
 */
export function ManageMembersDialog({ open, onOpenChange }: Props) {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<InvitableRole>("EDIT");
  const [selectedMember, setSelectedMember] = useState<WorkspaceMember | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch the member list once the dialog is opened. Powers the autocomplete
  // that distinguishes "existing member — change role" from "new email —
  // create invite".
  const { data: members = [] } = useMembers(open);

  const invite = useInviteMember();
  const sendEmail = useSendInviteEmail();
  const updateRole = useUpdateMemberRole();

  // Reset transient state whenever the dialog closes so the next open starts
  // from a clean slate (no stale copy-link chip, no stray success banner).
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedMember(null);
      setFeedback(null);
      setRole("EDIT");
    }
  }, [open]);

  // If the user picks a suggestion, mirror that member's current role into
  // the dropdown so the default action is "change role to <current>" — a
  // no-op unless they intentionally pick a different tier. Feels less
  // surprising than defaulting to EDIT.
  useEffect(() => {
    if (selectedMember && INVITABLE_ROLES.includes(selectedMember.role as InvitableRole)) {
      setRole(selectedMember.role as InvitableRole);
    }
  }, [selectedMember]);

  // Case-insensitive substring match on name + email. Hide already-suggested
  // once the user has selected them (the header renders the pill instead).
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as WorkspaceMember[];
    return members
      .filter((m) => m.role !== "ADMIN")               // can't change admin's role
      .filter((m) => {
        return (
          m.name?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q)
        );
      })
      .slice(0, 6);
  }, [members, query]);

  const trimmedQuery = query.trim();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQuery);
  const isExistingSelected = !!selectedMember;
  const primaryLabel = isExistingSelected ? "Change role" : "Create invite";
  const primaryDisabled =
    (isExistingSelected && selectedMember!.role === role) ||
    (!isExistingSelected && !looksLikeEmail);

  function pickMember(m: WorkspaceMember) {
    setSelectedMember(m);
    setQuery(`${m.name} <${m.email}>`);
    setFeedback(null);
  }

  function clearSelection() {
    setSelectedMember(null);
    setQuery("");
    setFeedback(null);
    inputRef.current?.focus();
  }

  async function runPrimary() {
    setFeedback(null);
    if (isExistingSelected) {
      try {
        await updateRole.mutateAsync({ id: selectedMember!.id, role });
        setFeedback({ kind: "roleChanged", name: selectedMember!.name, role });
      } catch (err) {
        setFeedback({ kind: "error", message: (err as Error).message || "Role change failed" });
      }
      return;
    }
    if (!looksLikeEmail) return;
    try {
      const res = await invite.mutateAsync({ email: trimmedQuery, role });
      setFeedback({
        kind: "invite",
        email: trimmedQuery,
        inviteUrl: res.inviteUrl,
        reused: !!res.alreadyExists,
      });
    } catch (err) {
      setFeedback({ kind: "error", message: (err as Error).message || "Invite failed" });
    }
  }

  async function copyLink() {
    if (feedback?.kind !== "invite") return;
    try {
      await navigator.clipboard.writeText(feedback.inviteUrl);
      // No banner — the button itself flips label to "Copied!" briefly.
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — the URL is still visible below the input */
    }
  }

  async function emailInvite() {
    if (feedback?.kind !== "invite") return;
    try {
      await sendEmail.mutateAsync({ token: extractToken(feedback.inviteUrl) });
      setFeedback({ kind: "emailSent", email: feedback.email });
    } catch (err) {
      setFeedback({ kind: "error", message: (err as Error).message || "Failed to send email" });
    }
  }

  const [copied, setCopied] = useState(false);

  // After a successful invite the modal switches into a "post-invite" state:
  // the input + primary action collapse and the URL / Copy / Send-email panel
  // takes over. Keeps the modal from stacking two visibly-separate cards
  // (the bug the user hit — feedback panel rendered under the buttons and
  // read as a second dialog).
  const inPostInvite = feedback?.kind === "invite" || feedback?.kind === "emailSent";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg gap-3 overflow-y-auto p-0">
        <DialogHeader className="border-b border-border p-4">
          <DialogTitle className="text-base">Manage members</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 px-4 pb-4">
          {inPostInvite && feedback?.kind === "invite" && (
            <PostInvitePanel
              email={feedback.email}
              inviteUrl={feedback.inviteUrl}
              reused={feedback.reused}
              copied={copied}
              onCopy={copyLink}
              onSend={emailInvite}
              onInviteAnother={() => {
                setFeedback(null);
                setQuery("");
                setSelectedMember(null);
                inputRef.current?.focus();
              }}
              onDone={() => onOpenChange(false)}
              sendPending={sendEmail.isPending}
            />
          )}

          {inPostInvite && feedback?.kind === "emailSent" && (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-600 dark:text-emerald-400">
                Email sent to {feedback.email}.
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFeedback(null);
                    setQuery("");
                    setSelectedMember(null);
                    inputRef.current?.focus();
                  }}
                >
                  Invite another
                </Button>
                <Button type="button" onClick={() => onOpenChange(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}

          {!inPostInvite && (
          <>
          {/* Input row — Name/email on the left, Role dropdown on the right. */}
          <div className="flex items-stretch gap-0 rounded-md border border-border bg-background focus-within:border-ring">
            {isExistingSelected ? (
              <div className="flex flex-1 items-center gap-2 px-3 py-2 text-sm">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/20 text-[10px] font-semibold uppercase text-primary">
                  {initials(selectedMember!.name)}
                </span>
                <span className="min-w-0 truncate font-medium text-foreground">
                  {selectedMember!.name}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {selectedMember!.email}
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  aria-label="Clear selection"
                  className="ml-auto grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setFeedback(null);
                }}
                placeholder="Name or email"
                autoComplete="off"
                spellCheck={false}
                className="flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
              />
            )}
            <RoleDropdown value={role} onChange={setRole} />
          </div>

          {/* Suggestions — only when typing and no member is picked yet. */}
          {!isExistingSelected && suggestions.length > 0 && (
            <div className="rounded-md border border-border bg-card shadow-sm">
              <div className="border-b border-border px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Existing members
              </div>
              <ul className="max-h-56 overflow-y-auto py-1">
                {suggestions.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => pickMember(m)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary"
                    >
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/20 text-[10px] font-semibold uppercase text-primary">
                        {initials(m.name)}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                        {m.name}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {m.email}
                      </span>
                      <span className="ml-2 rounded bg-secondary/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {ROLE_DISPLAY_NAMES[m.role as InvitableRole] || m.role}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contextual helper line */}
          {!isExistingSelected && trimmedQuery && !looksLikeEmail && suggestions.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Type a full email address to create an invite, or the name of an existing member to change their role.
            </p>
          )}
          {!isExistingSelected && looksLikeEmail && (
            <p className="text-xs text-muted-foreground">
              No existing member matches. A new invite will be created for{" "}
              <span className="font-medium text-foreground">{trimmedQuery}</span>.
            </p>
          )}
          {isExistingSelected && selectedMember!.role === role && (
            <p className="text-xs text-muted-foreground">
              {selectedMember!.name} is already{" "}
              <span className="font-medium text-foreground">{ROLE_DISPLAY_NAMES[role]}</span>. Pick a different role to change it.
            </p>
          )}

          {/* Primary action */}
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={runPrimary}
              disabled={primaryDisabled || invite.isPending || updateRole.isPending}
            >
              {(invite.isPending || updateRole.isPending) && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              {primaryLabel}
            </Button>
          </div>

          {/* In-modal feedback for the role-change + error cases only. The
              invite-success and email-sent cases are handled by the swap-in
              PostInvitePanel above so we never render two visually distinct
              cards inside one dialog. */}
          {feedback?.kind === "roleChanged" && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-600 dark:text-emerald-400">
              {feedback.name} is now {ROLE_DISPLAY_NAMES[feedback.role]}.
            </div>
          )}
          {feedback?.kind === "error" && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              {feedback.message}
            </div>
          )}
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Post-invite panel ────────────────────────────────────────────────────────

/**
 * Rendered in place of the input + primary buttons after a successful invite.
 * Owns the URL row + Copy + Send email + navigation (Invite another / Done).
 * Kept as its own component so the "one modal, two states" swap reads
 * cleanly at the top of ManageMembersDialog.
 */
function PostInvitePanel({
  email,
  inviteUrl,
  reused,
  copied,
  onCopy,
  onSend,
  onInviteAnother,
  onDone,
  sendPending,
}: {
  email: string;
  inviteUrl: string;
  reused: boolean;
  copied: boolean;
  onCopy: () => void;
  onSend: () => void;
  onInviteAnother: () => void;
  onDone: () => void;
  sendPending: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        {reused
          ? `Existing invite for ${email} — link reused.`
          : `Invite created for ${email}. Share the link or send the email.`}
      </div>

      {/* URL + Copy — single row, URL truncates so long tokens don't push
          Copy off-screen or force the modal to grow horizontally. */}
      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
          {inviteUrl}
        </span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onSend} disabled={sendPending}>
          {sendPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Mail className="h-3.5 w-3.5" />
          )}
          Send email
        </Button>
        <Button type="button" variant="ghost" onClick={onInviteAnother}>
          Invite another
        </Button>
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Role dropdown ────────────────────────────────────────────────────────────

function RoleDropdown({
  value,
  onChange,
}: {
  value: InvitableRole;
  onChange: (next: InvitableRole) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Select role"
        className="flex shrink-0 items-center gap-1 border-l border-border px-3 text-sm text-foreground transition-colors hover:bg-secondary/40"
      >
        <span className="truncate">{ROLE_DISPLAY_NAMES[value]}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {INVITABLE_ROLES.map((r) => (
          <DropdownMenuItem
            key={r}
            onSelect={() => onChange(r)}
            className="flex items-start gap-2"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">
                {ROLE_DISPLAY_NAMES[r]}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {ROLE_BLURBS[r]}
              </div>
            </div>
            {value === r && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Short blurbs mirroring the wording under each role in the permissions doc
// (workspace-scoped, kept in sync with worker/lib/permissions.js by hand).
const ROLE_BLURBS: Record<InvitableRole, string> = {
  FULL_ACCESS: "Can manage all resources within the workspace",
  EDIT_SHARE:  "Can manage resources, download, and share externally",
  EDIT:        "Can create, upload, and edit; can't share externally",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Pull the invite token out of the invite URL.
 * New format: `/invite/<token>` (token in the path).
 * Legacy fallback: `/invite.html?token=<token>` (query string).
 */
function extractToken(url: string): string {
  const q = url.match(/[?&]token=([^&]+)/);
  if (q) return decodeURIComponent(q[1]);
  const p = url.match(/\/invite\/([^/?#]+)/);
  return p ? decodeURIComponent(p[1]) : "";
}

function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  const first = parts[0][0] || "";
  const last = parts[parts.length - 1][0] || "";
  return (first + last).toUpperCase();
}

// Keep the `cn` import active if the file grows a tailwind-varying region;
// today it's only referenced by shared components. Silences the linter if
// unused imports become a rule later.
void cn;
